import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Home.css";
import backgroundStone from "../assets/images/background1.png";
import backgroundWater from "../assets/images/background2.png";
import Map from "../components/Map";
import Routes from "../components/Routes";

const GEO_PING_INTERVAL_MS = 10000;

function Home({ loggedInUser, onLogout }) {
  const [employeeId, setEmployeeId] = useState(loggedInUser?.employee_id || "");
  const [passcode, setPasscode] = useState(loggedInUser?.passcode || "");
  const [lastName, setLastName] = useState(loggedInUser?.last_name || "");
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [geoPermission, setGeoPermission] = useState("checking");
  const [locationStatus, setLocationStatus] = useState("Waiting for location permission...");
  const [currentPosition, setCurrentPosition] = useState(null);
  const [clockedInAt, setClockedInAt] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [statusMessage, setStatusMessage] = useState("Enter employee credentials to start the time clock.");

  const pingIntervalRef = useRef(null);
  const geoWatchRef = useRef(null);

  const formattedClock = useMemo(() => {
    return currentTime.toLocaleString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  }, [currentTime]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (loggedInUser) {
      setEmployeeId(loggedInUser.employee_id || "");
      setPasscode(loggedInUser.passcode || "");
      setLastName(loggedInUser.last_name || "");
      setStatusMessage(
        `Signed in as ${loggedInUser.employee_id}${loggedInUser.last_name ? ` (${loggedInUser.last_name})` : ""}. Ready to clock in.`
      );
    }
  }, [loggedInUser]);

  useEffect(() => {
    requestGeolocationPermission();

    return () => {
      stopTracking();
    };
  }, []);

  const requestGeolocationPermission = async () => {
    if (!("geolocation" in navigator)) {
      setGeoPermission("unsupported");
      setLocationStatus("Geolocation is not supported on this device or browser.");
      return;
    }

    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permissionResult = await navigator.permissions.query({ name: "geolocation" });
        setGeoPermission(permissionResult.state);

        permissionResult.onchange = () => {
          setGeoPermission(permissionResult.state);
        };
      }
    } catch (error) {
      console.error("Permission check failed:", error);
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        setGeoPermission("granted");
        setCurrentPosition({ latitude, longitude, accuracy: position.coords.accuracy });
        setLocationStatus(
          `Live location ready: ${latitude.toFixed(6)}, ${longitude.toFixed(6)} (±${Math.round(
            position.coords.accuracy
          )}m)`
        );
      },
      (error) => {
        console.error("Geolocation error:", error);
        setGeoPermission("denied");
        setLocationStatus("Location permission was denied or unavailable. Enable location access to use the time clock.");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const startTracking = () => {
    if (!("geolocation" in navigator)) {
      return;
    }

    if (geoWatchRef.current === null) {
      geoWatchRef.current = navigator.geolocation.watchPosition(
        (position) => {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          const accuracy = position.coords.accuracy;

          setCurrentPosition({ latitude, longitude, accuracy });
          setLocationStatus(
            `Tracking active: ${latitude.toFixed(6)}, ${longitude.toFixed(6)} (±${Math.round(accuracy)}m)`
          );
        },
        (error) => {
          console.error("Watch position error:", error);
          setLocationStatus("Unable to keep tracking location. Check device permissions and GPS signal.");
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }

    if (pingIntervalRef.current === null) {
      pingIntervalRef.current = setInterval(() => {
        sendGeoPing();
      }, GEO_PING_INTERVAL_MS);
    }
  };

  const stopTracking = () => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (geoWatchRef.current !== null && "geolocation" in navigator) {
      navigator.geolocation.clearWatch(geoWatchRef.current);
      geoWatchRef.current = null;
    }
  };

  const sendGeoPing = async () => {
    if (!currentPosition || !employeeId || !lastName || !isClockedIn) {
      return;
    }

    try {
      await fetch("/api/timeclock/ping", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          employee_id: employeeId,
          employee_last_name: lastName,
          latitude: currentPosition.latitude,
          longitude: currentPosition.longitude,
          accuracy: currentPosition.accuracy,
          pinged_at: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error("Geo ping failed:", error);
    }
  };

  const handleClockIn = async (event) => {
    event.preventDefault();

    if (!employeeId.trim() || !passcode.trim() || !lastName.trim()) {
      setStatusMessage("Employee ID, passcode, and last name are required.");
      return;
    }

    if (geoPermission === "denied" || geoPermission === "unsupported") {
      setStatusMessage("Location access is required before clocking in.");
      return;
    }

    if (!currentPosition) {
      setStatusMessage("Waiting for live GPS position. Please allow location access and try again.");
      requestGeolocationPermission();
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        employee_id: employeeId,
        passcode,
        employee_last_name: lastName,
        latitude: currentPosition.latitude,
        longitude: currentPosition.longitude,
        accuracy: currentPosition.accuracy,
        clocked_in_at: new Date().toISOString(),
      };

      const response = await fetch("/api/timeclock/clock-in", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Clock-in request failed.");
      }

      setIsClockedIn(true);
      setClockedInAt(new Date());
      setStatusMessage("Clocked in successfully. GPS movement logging is active every 10 seconds.");
      startTracking();
      await sendGeoPing();
    } catch (error) {
      console.error("Clock in failed:", error);
      setStatusMessage("Clock-in could not be completed. Check credentials and backend endpoint.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClockOut = async () => {
    setIsSubmitting(true);

    try {
      await fetch("/api/timeclock/clock-out", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          employee_id: employeeId,
          employee_last_name: lastName,
          clocked_out_at: new Date().toISOString(),
          latitude: currentPosition?.latitude || null,
          longitude: currentPosition?.longitude || null,
          accuracy: currentPosition?.accuracy || null,
        }),
      });
    } catch (error) {
      console.error("Clock out failed:", error);
    } finally {
      stopTracking();
      setIsClockedIn(false);
      setClockedInAt(null);
      setStatusMessage("Clocked out. GPS movement logging has stopped.");
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="home-page"
      style={{
        "--stone-bg": `url(${backgroundStone})`,
        "--water-bg": `url(${backgroundWater})`,
      }}
    >
      <div className="home-water-layer" />
      <div className="home-backdrop" />
      <div className="home-grid" />

      <div className="home-content">
        <section className="hero-shell">
          <div className="hero-panel transparent-panel">
            <div className="hero-eyebrow">Graystone Marketing Team</div>
            <h1 className="hero-title">
              Field <span className="accent">Time Clock</span>
            </h1>
            <p className="hero-subtitle">
              Secure employee check-in with live GPS permission, location stamping, and route movement tracking.
            </p>
            <div className="timeclock-status" style={{ marginTop: 18 }}>
              <strong>Signed In User:</strong> {employeeId || "Not available"}
              {lastName ? ` | ${lastName}` : ""}
            </div>

            <div className="section-shell glow" style={{ marginTop: 24 }}>
              <div className="highlight-line" />
              <h2 className="section-title">Live System Clock</h2>
              <p className="section-copy">{formattedClock}</p>
            </div>

            <div className="glass-grid">
              <div className="glass-card permission-card">
                <span className="card-kicker">Permission</span>
                <h3 className="card-title">Location Access</h3>
                <p className="card-copy">{geoPermission}</p>
              </div>

              <div className="glass-card tracking-card">
                <span className="card-kicker">GPS Status</span>
                <h3 className="card-title">Tracking</h3>
                <p className="card-copy">{locationStatus}</p>
              </div>

              <div className="glass-card session-card">
                <span className="card-kicker">Session</span>
                <h3 className="card-title">Clock State</h3>
                <p className="card-copy">
                  {isClockedIn && clockedInAt
                    ? `Active since ${clockedInAt.toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                        second: "2-digit",
                      })}`
                    : "Employee is currently clocked out."}
                </p>
              </div>
            </div>

            <div className="section-shell transparent timeclock-shell">
              <div className="highlight-line" />
              <h2 className="section-title">Employee Check-In</h2>
              <p className="section-copy" style={{ marginBottom: 24 }}>
                Employee credentials are pulled from the active login session. Once clocked in, the app sends GPS pings every 10 seconds while it remains open.
              </p>

              <form onSubmit={handleClockIn}>
                <div className="timeclock-form-grid">
                  <div className="timeclock-field">
                    <label htmlFor="employeeId" className="timeclock-label">
                      Employee ID #
                    </label>
                    <input
                      id="employeeId"
                      type="text"
                      value={employeeId}
                      onChange={(event) => setEmployeeId(event.target.value)}
                      disabled={Boolean(loggedInUser)}
                      placeholder="Enter employee ID"
                      className="timeclock-input"
                    />
                  </div>

                  <div className="timeclock-field">
                    <label htmlFor="passcode" className="timeclock-label">
                      Employee Passcode
                    </label>
                    <input
                      id="passcode"
                      type="password"
                      value={passcode}
                      onChange={(event) => setPasscode(event.target.value)}
                      disabled={Boolean(loggedInUser)}
                      placeholder="Enter passcode"
                      className="timeclock-input"
                    />
                  </div>

                  <div className="timeclock-field">
                    <label htmlFor="lastName" className="timeclock-label">
                      Employee Last Name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      disabled={Boolean(loggedInUser)}
                      placeholder="Enter last name"
                      className="timeclock-input"
                    />
                  </div>
                </div>

                <div className="timeclock-actions">
                  <button className="glow-button" type="submit" disabled={isSubmitting || isClockedIn}>
                    {isSubmitting && !isClockedIn ? "Clocking In..." : "Clock In"}
                  </button>

                  <button
                    className="ghost-button"
                    type="button"
                    onClick={handleClockOut}
                    disabled={isSubmitting || !isClockedIn}
                  >
                    {isSubmitting && isClockedIn ? "Clocking Out..." : "Clock Out"}
                  </button>

                  <button className="ghost-button" type="button" onClick={requestGeolocationPermission}>
                    Refresh GPS Permission
                  </button>

                  {onLogout ? (
                    <button className="ghost-button" type="button" onClick={onLogout}>
                      Sign Out
                    </button>
                  ) : null}
                </div>
              </form>

              <div className="timeclock-status">{statusMessage}</div>

              {currentPosition && (
                <div className="timeclock-coordinates">
                  <strong>Latest Coordinates:</strong> {currentPosition.latitude.toFixed(6)}, {" "}
                  {currentPosition.longitude.toFixed(6)} | Accuracy ±{Math.round(currentPosition.accuracy)}m
                </div>
              )}
            </div>
          </div>
        </section>

        <Map
          routeId="92154-R029"
          destination={{
            title: "Marked Location",
            street: "1222 Seahill Ct",
            cityStateZip: "San Diego, CA 92154",
            country: "United States",
          }}
        />

        <Routes />
      </div>
    </div>
  );
}

export default Home;